import { computed, shallowRef, watch, type Ref } from "vue";
import type { GeoJSONSource, Map as MapboxMap, MapMouseEvent } from "mapbox-gl";
import type { Feature, FeatureCollection } from "geojson";
import { withValidPointPositions } from "../utils/mapZoom";
import { mapCountLabelLayout, mapCountTextField } from "../utils/mapLabel";
import { spiderfyLegOffsets } from "../utils/mapSpiderfy";
import { bindMapPinImage, mapPinSymbolLayout, MAP_PIN_IMAGE_URL, MAP_PIN_WIDTH } from "../utils/mapPinImage";
import {
  bindPointerCursor,
  EMPTY_FEATURE_COLLECTION,
  MAP_LABEL_COLOR,
  MAP_POINT_COLOR,
  POINT_CIRCLE_RADIUS,
  POINT_HIT_STROKE,
  removeLayersIfPresent,
  removeSourcesIfPresent,
  useAppMapStyleLayers,
} from "./useAppMapStyleLayers";

const GROUP_SOURCE_ID = "sites-group";
const LAYER_GROUP_CIRCLES = "sites-group-circles";
const LAYER_GROUP_COUNT = "sites-group-count";
const LAYER_GROUP_ACTIVE_PIN = "sites-group-active-pin";
const GROUP_ACTIVE_PIN_IMAGE_ID = "sites-group-active-pin-image";
const SPIDER_SOURCE_ID = "sites-spider";
const LAYER_SPIDER_GROUP = "sites-spider-group";
const LAYER_SPIDER_GROUP_COUNT = "sites-spider-group-count";
const LAYER_SPIDER_LEGS = "sites-spider-legs";

export const GROUP_CIRCLE_RADIUS = 20;
export const GROUP_COUNT_SIZE = 14;
// Un groupe dont l'un des points est actif porte lui-même la sélection, couleurs inversées, plutôt
// que de laisser un marqueur actif se poser par-dessus lui.
const ACTIVE_GROUP_COLOR = MAP_LABEL_COLOR;
const ACTIVE_GROUP_COUNT_COLOR = MAP_POINT_COLOR;
const ACTIVE_POINT_PROPERTY = "isActive";
const ACTIVE_COUNT_PROPERTY = "activeCount";
// Le cercle du groupe se pose sur le barycentre de ses points, qui eux restent dessinés là où ils
// sont — et le point actif l'est par un marqueur bien plus large qu'une pastille, que le moindre
// écart ferait dépasser. Seuls les points confondus, qu'aucun zoom ne sépare, sont donc regroupés :
// le rayon reste sous le pixel d'écran, y compris au facteur deux qui sépare deux niveaux entiers
// de zoom (le regroupement est calculé par tuile, à l'échelle du niveau inférieur).
const GROUP_RADIUS = 2;
const SPIDER_TRANSITION_MS = 250;
// Le compteur s'efface deux fois plus vite que le cercle ne se contracte : il déborderait sinon.
const COUNT_FADE_RATE = 2;
// Le cercle du groupe éclaté est repris par la source des groupes, dont la réindexation est
// asynchrone : le sosie de l'escargot lui laisse le temps d'arriver avant de s'effacer.
const GROUP_HANDOVER_MS = 150;
const SPIDER_GROUP_PROPERTY = "spiderGroup";
const SPIDER_GROUP_LABEL = "spiderGroupLabel";
// Le regroupement doit tenir jusqu'au zoom maximal de Mapbox : des points strictement confondus ne
// se séparent jamais, l'éclatement en escargot est leur seule issue. `maxzoom` doit dépasser
// `clusterMaxZoom`, sinon les tuiles du dernier niveau sont réutilisées en sur-zoom et le
// regroupement se fige sur celui d'un zoom plus lointain.
const GROUP_CLUSTER_MAX_ZOOM = 22;
const GROUP_SOURCE_MAX_ZOOM = 23;

interface UseAppMapSpiderfyOptions {
  data: Ref<FeatureCollection | undefined>;
  activeFeatureId?: Ref<number | string | undefined>;
  onFeatureClick?: (payload: { feature: Feature; event: MapMouseEvent }) => void;
  countProperty?: string;
  localGlyphs?: Ref<boolean>;
  minZoom: number;
}

interface SpiderLeg {
  id: string;
  properties: Feature["properties"];
  screenOffset: [number, number];
}

interface Spider {
  anchor: [number, number];
  label: string;
  legs: SpiderLeg[];
}

function easeOutCubic(ratio: number): number {
  return 1 - (1 - ratio) ** 3;
}

export function useAppMapSpiderfy(map: Ref<MapboxMap | undefined>, options: UseAppMapSpiderfyOptions) {
  // Les identifiants disent quels points sont éclatés — ils ne bougent qu'aux deux extrémités de la
  // transition, quand les sources doivent les lâcher ou les reprendre. Les positions, elles, sont
  // réécrites à chaque image : seul le pin actif les suit, ce qui ne coûte qu'une feature.
  const spiderfiedIds = shallowRef(new Set<string>());
  const spiderfiedPositions = shallowRef(new Map<string, [number, number]>());
  let spider: Spider | undefined;
  let spiderProgress = 0;
  let transitionFrame: number | undefined;
  let handoverTimeout: ReturnType<typeof setTimeout> | undefined;

  const validData = computed(() => withValidPointPositions(options.data.value ?? EMPTY_FEATURE_COLLECTION));

  function resetSpider() {
    spider = undefined;
    spiderProgress = 0;
    if (spiderfiedIds.value.size) spiderfiedIds.value = new Set();
    if (spiderfiedPositions.value.size) spiderfiedPositions.value = new Map();
  }

  // Seuls les points éclatés quittent la source des groupes, le temps que les pattes les rendent :
  // un point actif reste compté par son groupe, qu'il vienne d'être replié ou n'ait jamais été
  // ouvert. Les retirer de la source plutôt que filtrer les calques tient à travers les
  // réindexations que provoque chaque rafraîchissement des données.
  function groupData(): FeatureCollection {
    const data = validData.value;
    const spiderfied = spiderfiedIds.value;
    const activeId = options.activeFeatureId?.value;
    return {
      ...data,
      features: data.features.flatMap((f) => {
        const id = String(f.properties?.id);
        if (spiderfied.has(id)) return [];
        if (activeId == null || id !== String(activeId)) return [f];
        return [{ ...f, properties: { ...f.properties, [ACTIVE_POINT_PROPERTY]: 1 } }];
      }),
    };
  }

  function renderGroups(m: MapboxMap) {
    (m.getSource(GROUP_SOURCE_ID) as GeoJSONSource | undefined)?.setData(groupData());
  }

  function applyGroupContraction(m: MapboxMap) {
    if (!m.getLayer(LAYER_SPIDER_GROUP)) return;
    m.setPaintProperty(LAYER_SPIDER_GROUP, "circle-radius", GROUP_CIRCLE_RADIUS * (1 - spiderProgress));
    m.setPaintProperty(LAYER_SPIDER_GROUP_COUNT, "text-opacity", Math.max(0, 1 - spiderProgress * COUNT_FADE_RATE));
  }

  function renderSpider(m: MapboxMap) {
    const source = m.getSource(SPIDER_SOURCE_ID) as GeoJSONSource | undefined;
    if (!spider) {
      if (spiderfiedPositions.value.size) spiderfiedPositions.value = new Map();
      source?.setData(EMPTY_FEATURE_COLLECTION);
      return;
    }

    const { anchor, label } = spider;
    const anchorPoint = m.project(anchor);
    const positions = new Map<string, [number, number]>();
    const features: Feature[] = spider.legs.map((leg) => {
      const { lng, lat } = m.unproject([
        anchorPoint.x + leg.screenOffset[0] * spiderProgress,
        anchorPoint.y + leg.screenOffset[1] * spiderProgress,
      ]);
      positions.set(leg.id, [lng, lat]);
      return { type: "Feature", properties: leg.properties, geometry: { type: "Point", coordinates: [lng, lat] } };
    });

    features.push({
      type: "Feature",
      properties: { [SPIDER_GROUP_PROPERTY]: true, [SPIDER_GROUP_LABEL]: label },
      geometry: { type: "Point", coordinates: anchor },
    });

    spiderfiedPositions.value = positions;
    source?.setData({ type: "FeatureCollection", features });
    applyGroupContraction(m);
  }

  function stopTransition() {
    if (transitionFrame != null) cancelAnimationFrame(transitionFrame);
    transitionFrame = undefined;
  }

  function stopHandover() {
    if (handoverTimeout != null) clearTimeout(handoverTimeout);
    handoverTimeout = undefined;
  }

  function transitionTo(m: MapboxMap, target: number, onDone?: () => void) {
    stopTransition();
    const from = spiderProgress;
    const startedAt = performance.now();
    const step = (now: number) => {
      const ratio = Math.min(1, (now - startedAt) / SPIDER_TRANSITION_MS);
      spiderProgress = from + (target - from) * easeOutCubic(ratio);
      renderSpider(m);
      if (ratio < 1) {
        transitionFrame = requestAnimationFrame(step);
        return;
      }
      transitionFrame = undefined;
      onDone?.();
    };
    transitionFrame = requestAnimationFrame(step);
  }

  function handOverToGroups(m: MapboxMap) {
    if (spider) spider = { ...spider, legs: [] };
    spiderProgress = 0;
    spiderfiedIds.value = new Set();
    renderSpider(m);
    renderGroups(m);
    handoverTimeout = setTimeout(() => {
      handoverTimeout = undefined;
      spider = undefined;
      if (map.value) renderSpider(map.value);
    }, GROUP_HANDOVER_MS);
  }

  function collapse(m: MapboxMap, { animated = true } = {}) {
    if (!spider?.legs.length) return;
    if (!animated) {
      stopTransition();
      stopHandover();
      resetSpider();
      renderSpider(m);
      renderGroups(m);
      return;
    }
    transitionTo(m, 0, () => handOverToGroups(m));
  }

  function layoutLegs(m: MapboxMap, group: { anchor: [number, number]; label: string }, leaves: Feature[]) {
    const offsets = spiderfyLegOffsets(leaves.length);
    const legs = leaves.flatMap((leaf, index) => {
      const screenOffset = offsets[index];
      const id = leaf.properties?.id;
      if (!screenOffset || id == null) return [];
      return [{ id: String(id), properties: leaf.properties, screenOffset }];
    });
    if (!legs.length) return;

    stopHandover();
    spider = { ...group, legs };
    spiderProgress = 0;
    spiderfiedIds.value = new Set(legs.map((leg) => leg.id));
    renderGroups(m);
    transitionTo(m, 1);
  }

  function groupLabel(properties: Feature["properties"]): string {
    const countProperty = options.countProperty;
    const value = countProperty ? properties?.[countProperty] : (properties?.point_count_abbreviated ?? properties?.point_count);
    return String(value ?? "");
  }

  function expand(m: MapboxMap, group: Feature) {
    if (group.geometry.type !== "Point") return;
    const clusterId = group.properties?.cluster_id as number | undefined;
    const count = group.properties?.point_count as number | undefined;
    const source = m.getSource(GROUP_SOURCE_ID) as GeoJSONSource | undefined;
    if (clusterId == null || !count || !source) return;
    const anchor = group.geometry.coordinates as [number, number];
    const label = groupLabel(group.properties);
    source.getClusterLeaves(clusterId, count, 0, (error, leaves) => {
      if (error || !leaves?.length) return;
      layoutLegs(m, { anchor, label }, leaves as Feature[]);
    });
  }

  // La patte active laisse sa place au pin qu'AppMap pose sur sa position éclatée. `addLayer`
  // n'accepte pas de filtre nul, d'où le prédicat toujours vrai en l'absence de point actif.
  function legFilter() {
    const isLeg = ["!", ["has", SPIDER_GROUP_PROPERTY]];
    const id = options.activeFeatureId?.value;
    return id == null ? isLeg : ["all", isLeg, ["!=", ["to-string", ["get", "id"]], String(id)]];
  }

  function hitsSpiderfyLayer(point: MapMouseEvent["point"]): boolean {
    const m = map.value;
    if (!m) return false;
    const layers = [LAYER_GROUP_CIRCLES, LAYER_SPIDER_LEGS].filter((id) => m.getLayer(id));
    return layers.length > 0 && m.queryRenderedFeatures(point, { layers }).length > 0;
  }

  function countLabelLayout(textField: ReturnType<typeof mapCountTextField>) {
    return mapCountLabelLayout({ localGlyphs: options.localGlyphs?.value ?? false, textField, textSize: GROUP_COUNT_SIZE });
  }

  function setup(m: MapboxMap) {
    const countProperty = options.countProperty;
    const holdsActivePoint = [">", ["coalesce", ["get", ACTIVE_COUNT_PROPERTY], 0], 0];
    const unbindImage = bindMapPinImage(m, { id: GROUP_ACTIVE_PIN_IMAGE_ID, url: MAP_PIN_IMAGE_URL, width: MAP_PIN_WIDTH });
    m.addSource(GROUP_SOURCE_ID, {
      type: "geojson",
      data: groupData(),
      cluster: true,
      clusterRadius: GROUP_RADIUS,
      clusterMaxZoom: GROUP_CLUSTER_MAX_ZOOM,
      maxzoom: GROUP_SOURCE_MAX_ZOOM,
      clusterProperties: {
        [ACTIVE_COUNT_PROPERTY]: ["+", ["coalesce", ["get", ACTIVE_POINT_PROPERTY], 0]],
        ...(countProperty && { [countProperty]: ["+", ["coalesce", ["get", countProperty], 1]] }),
      },
    });
    m.addSource(SPIDER_SOURCE_ID, { type: "geojson", data: EMPTY_FEATURE_COLLECTION });

    m.addLayer({
      id: LAYER_GROUP_CIRCLES,
      type: "circle",
      source: GROUP_SOURCE_ID,
      filter: ["has", "point_count"],
      paint: {
        "circle-color": ["case", holdsActivePoint, ACTIVE_GROUP_COLOR, MAP_POINT_COLOR],
        "circle-radius": ["step", ["zoom"], 0, options.minZoom, GROUP_CIRCLE_RADIUS],
      },
    });

    m.addLayer({
      id: LAYER_GROUP_COUNT,
      type: "symbol",
      source: GROUP_SOURCE_ID,
      filter: ["has", "point_count"],
      layout: countLabelLayout(mapCountTextField(countProperty)),
      paint: {
        "text-color": ["case", holdsActivePoint, ACTIVE_GROUP_COUNT_COLOR, MAP_LABEL_COLOR],
        "text-opacity": ["step", ["zoom"], 0, options.minZoom, 1],
      },
    });

    // Le point actif que le regroupement a laissé isolé : son marqueur est posé ici plutôt que par
    // AppMap, pour qu'il s'efface de lui-même dès qu'un groupe l'absorbe.
    m.addLayer({
      id: LAYER_GROUP_ACTIVE_PIN,
      type: "symbol",
      source: GROUP_SOURCE_ID,
      filter: ["all", ["!", ["has", "point_count"]], ["has", ACTIVE_POINT_PROPERTY]],
      layout: mapPinSymbolLayout(GROUP_ACTIVE_PIN_IMAGE_ID),
      paint: {
        "icon-opacity": ["step", ["zoom"], 0, options.minZoom, 1],
        "icon-opacity-transition": { duration: 0 },
      },
    });

    // Sosie du cercle du groupe éclaté : il seul se contracte pendant que les pattes s'écartent,
    // là où le vrai cercle, une feature parmi d'autres du calque des groupes, ne peut être animé.
    m.addLayer({
      id: LAYER_SPIDER_GROUP,
      type: "circle",
      source: SPIDER_SOURCE_ID,
      filter: ["has", SPIDER_GROUP_PROPERTY],
      paint: {
        "circle-color": MAP_POINT_COLOR,
        "circle-radius": GROUP_CIRCLE_RADIUS,
        "circle-radius-transition": { duration: 0 },
      },
    });

    m.addLayer({
      id: LAYER_SPIDER_GROUP_COUNT,
      type: "symbol",
      source: SPIDER_SOURCE_ID,
      filter: ["has", SPIDER_GROUP_PROPERTY],
      layout: countLabelLayout(["get", SPIDER_GROUP_LABEL]),
      paint: {
        "text-color": MAP_LABEL_COLOR,
        "text-opacity-transition": { duration: 0 },
      },
    });

    m.addLayer({
      id: LAYER_SPIDER_LEGS,
      type: "circle",
      source: SPIDER_SOURCE_ID,
      filter: legFilter() as never,
      paint: {
        "circle-color": MAP_POINT_COLOR,
        "circle-radius": POINT_CIRCLE_RADIUS,
        "circle-stroke-width": POINT_HIT_STROKE,
        "circle-stroke-opacity": 0,
      },
    });

    applyGroupContraction(m);

    const onGroupClick = (e: MapMouseEvent) => {
      const group = m.queryRenderedFeatures(e.point, { layers: [LAYER_GROUP_CIRCLES] })[0];
      if (group) expand(m, group as Feature);
    };

    const onLegClick = (e: MapMouseEvent) => {
      const feature = m.queryRenderedFeatures(e.point, { layers: [LAYER_SPIDER_LEGS] })[0];
      if (!feature) return;
      options.onFeatureClick?.({ feature: feature as Feature, event: e });
    };

    const onMapClick = (e: MapMouseEvent) => {
      if (!hitsSpiderfyLayer(e.point)) collapse(m);
    };

    // Les pattes sont posées en coordonnées géographiques à partir d'un décalage en pixels : le
    // déplacement les conserve, un changement d'échelle les fausserait — et le repli, animé sur une
    // échelle qui bouge, décrocherait des points.
    const onZoomStart = () => collapse(m, { animated: false });

    const unbindCursor = bindPointerCursor(m, LAYER_GROUP_CIRCLES, LAYER_SPIDER_LEGS);
    m.on("click", LAYER_GROUP_CIRCLES, onGroupClick);
    m.on("click", LAYER_SPIDER_LEGS, onLegClick);
    m.on("click", onMapClick);
    m.on("zoomstart", onZoomStart);

    return () => {
      stopTransition();
      stopHandover();
      unbindImage();
      unbindCursor();
      m.off("click", LAYER_GROUP_CIRCLES, onGroupClick);
      m.off("click", LAYER_SPIDER_LEGS, onLegClick);
      m.off("click", onMapClick);
      m.off("zoomstart", onZoomStart);
      removeLayersIfPresent(
        m,
        LAYER_SPIDER_LEGS,
        LAYER_SPIDER_GROUP_COUNT,
        LAYER_SPIDER_GROUP,
        LAYER_GROUP_ACTIVE_PIN,
        LAYER_GROUP_COUNT,
        LAYER_GROUP_CIRCLES
      );
      removeSourcesIfPresent(m, SPIDER_SOURCE_ID, GROUP_SOURCE_ID);
      resetSpider();
    };
  }

  useAppMapStyleLayers(map, setup);

  // Un rafraîchissement des données (recadrage → nouvelle bbox) ne doit pas refermer l'escargot :
  // seules les pattes dont le point a disparu du jeu de données sont retirées.
  watch(validData, (data) => {
    const m = map.value;
    if (!m) return;
    if (spider?.legs.length) {
      const availableIds = new Set(data.features.map((f) => String(f.properties?.id)));
      const legs = spider.legs.filter((leg) => availableIds.has(leg.id));
      if (legs.length) {
        spider = { ...spider, legs };
      } else {
        stopTransition();
        resetSpider();
      }
    }
    renderSpider(m);
    renderGroups(m);
  });

  if (options.activeFeatureId) {
    watch(options.activeFeatureId, () => {
      const m = map.value;
      if (!m) return;
      if (m.getLayer(LAYER_SPIDER_LEGS)) m.setFilter(LAYER_SPIDER_LEGS, legFilter() as never);
      renderGroups(m);
    });
  }

  return { spiderfiedIds, spiderfiedPositions, hitsSpiderfyLayer };
}
